import { Map, Marker } from 'mapbox-gl';
import * as mapbox from 'mapbox-gl';
import { DataKeyValueStringInterface } from 'pf-frontend-common/src/module/data/key-value/string.interface';
import { DomElementEvent } from 'pf-frontend-common/src/module/dom/element.event';
import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { BrowserDocumentServiceInterface } from 'pf-frontend-common/src/service/browser-document/service.interface';
import { WindowServiceInterface } from 'pf-frontend-common/src/service/window/service.interface';

import { BackendConfigLanguageInterface } from 'common/data/backend/config/language.interface';
import { MapCoordinatesInterface } from 'common/module/map/coordinates.interface';
import { MapMarkerSettingsInterface } from 'common/module/map/marker/settings.interface';
import { MapViewEvent } from 'common/module/map/view.event';
import { MapViewInterface } from 'common/module/map/view.interface';
import { MapViewOptionsInterface } from 'common/module/map/view-options.interface';
import { MapboxMapsCss } from 'common/module/mapbox/maps/css';
import { HeaderServiceInterface } from 'common/service/header/service.interface';
import { StatsServiceInterface } from 'common/service/stats/service.interface';

import { MapboxMapsControlPositionTypeEnum } from './control-position-type.enum';

/**
 * Basic Mapbox Maps view
 */
export class MapboxMapsView implements MapViewInterface {
  /**
   * Maximum zoom value supported by Mapbox Maps
   */
  public static readonly ZOOM_MAX: number = 18;

  /**
   * Map view type id
   */
  public static readonly MAP_ID: string = 'mapbox://styles/propertyfindermap/cju6mojts1tkj1fmnoxcuyrgp?optimize=true';

  /**
   * Map view type id for rtl
   */
  public static readonly MAP_ID_RTL: string =
    'mapbox://styles/propertyfindermap/cjuqww67l18pz1fp9fhhwug7z?optimize=true';

  /**
   * New propertyfinder logo map marker
   */
  public static readonly LOGO_MAKRER: string = __('assets/common/map-marker.svg');

  /**
   * Map rtl plugin
   */
  public static readonly MAP_RTL_PLUGIN: string =
    'https://api.mapbox.com/mapbox-gl-js/plugins/mapbox-gl-rtl-text/v0.2.0/mapbox-gl-rtl-text.js';

  /**
   * View container element
   */
  protected element: HTMLElement;

  /**
   * Map initial settings
   */
  protected settings: MapMarkerSettingsInterface;

  /**
   * Cached scroll position
   */
  protected scrollY: number = null;

  /**
   * Map constructor
   */
  protected map: Map;

  /**
   * Marker constructor
   */
  protected MarkerConstructor: typeof Marker;

  /**
   * Array of added markers coordinate
   */
  protected markersCoordinates: MapCoordinatesInterface[] = [];

  /**
   * Array of added markers
   */
  protected markers: Marker[] = [];

  /**
   * CSS Classes
   */
  protected cssClass: DataKeyValueStringInterface = {
    containerFullscreen: 'map__container--fullscreen',
    mapFullscreen: 'map--fullscreen',
    buttonHide: 'map__button--hidden',
    titleHide: 'map__title--hidden',
    mapHidden: 'map--hidden',
    placeholderLoading: 'map__placeholder--loading',
    placeholderButtonHide: 'map__placeholder-button--hidden',
  };

  /**
   * CSS selectors
   */
  protected cssSelector: DataKeyValueStringInterface = {
    map: '[data-qs="map-content"]',
    buttonEnterFullscreen: '[data-qs="map-button-enter-fullscreen"]',
    buttonExitFullscreen: '[data-qs="map-button-exit-fullscreen"]',
    title: '[data-qs="map-title"]',
    showMapButton: '[data-qs="map-button-show"]',
    mapContainer: '[data-qs="map_container"]',
    mapPlaceholder: '[data-qs="map_placeholder"]',
  };

  /**
   * Element to hold map
   */
  protected mapElement: HTMLElement;

  /**
   * Element to hold map and buttons
   */
  protected mapContainer: HTMLElement;

  /**
   * Button to trigger enter fullscreen
   */
  protected buttonEnterFullscreenElement: HTMLElement;

  /**
   * Button to trigger exit fullscreen
   */
  protected buttonExitFullscreenElement: HTMLElement;

  /**
   * Map title element in collapsed view
   */
  protected titleElement: HTMLElement;

  /**
   * Map placeholder element
   */
  protected placeholderElement: HTMLElement;

  /**
   * Show map button element
   */
  protected showMapButtonElement: HTMLElement;

  /**
   * Is the map in its full screen state
   */
  protected isMapFullScreen: boolean = false;

  /**
   * Show map on page load
   */
  protected showOnPageLoad: boolean;

  /**
   * Constructor
   */
  constructor(
    protected eventEmitter: EventEmitterInterface,
    protected documentService: BrowserDocumentServiceInterface,
    protected headerService: HeaderServiceInterface,
    protected mapboxMapsCss: MapboxMapsCss,
    protected windowService: WindowServiceInterface,
    protected mapboxAccessToken: string,
    protected domQuerySelector: (el: HTMLElement, selector: string) => HTMLElement,
    protected domQuerySelectorAll: (el: HTMLElement, selector: string) => HTMLElement[],
    private statsService: StatsServiceInterface,
    protected configGetLanguage: BackendConfigLanguageInterface
  ) {}

  /**
   * @inheritDoc
   */
  public initialize(options: MapViewOptionsInterface): void {
    this.element = options.element;
    this.settings = options.settings;
    this.showOnPageLoad = options.showOnPageLoad;

    if (!this.element) {
      return;
    }

    // Find elements inside the view
    this.mapElement = this.domQuerySelector(this.element, this.cssSelector.map);
    this.mapContainer = this.domQuerySelector(this.element, this.cssSelector.mapContainer);
    this.buttonEnterFullscreenElement = this.domQuerySelector(this.element, this.cssSelector.buttonEnterFullscreen);
    this.buttonExitFullscreenElement = this.domQuerySelector(this.element, this.cssSelector.buttonExitFullscreen);
    this.titleElement = this.domQuerySelector(this.element, this.cssSelector.title);
    this.placeholderElement = this.domQuerySelector(this.element, this.cssSelector.mapPlaceholder);
    this.showMapButtonElement = this.domQuerySelector(this.element, this.cssSelector.showMapButton);

    // Attach observers
    this.observers();

    if (this.showOnPageLoad) {
      this.render();
    }
  }

  /**
   * @inheritDoc
   */
  public render(): void {
    if (!this.element) {
      return;
    }

    this.setLoading(true);

    // Load script
    this.mapboxMapsCss.load().then(this.onLoadMapboxMapsCss);
  }

  /**
   * @inheritDoc
   */
  public refresh(): void {
    // Force maps to refresh its container
    this.map.resize();
    this.setZoom(this.settings.zoom.percentage);
  }

  /**
   * @inheritDoc
   */
  public getEventEmitter(): EventEmitterInterface {
    return this.eventEmitter;
  }

  /**
   * @inheritDoc
   */
  public setCenter(coordinates: MapCoordinatesInterface): void {
    this.map.setCenter([coordinates.lon, coordinates.lat]);
  }

  /**
   * @inheritDoc
   */
  public setZoom(zoomPercentage: number): void {
    this.map.setZoom(this.getZoomValue(zoomPercentage));
  }

  /**
   * @inheritDoc
   */
  public setFullScreen(isMapFullScreen: boolean): void {
    if (isMapFullScreen === this.isMapFullScreen) {
      return;
    }

    this.isMapFullScreen = isMapFullScreen;

    this.setCenter(this.settings.center.coordinates);

    this.getEventEmitter().emit(MapViewEvent.toggleFullscreen);

    if (this.isMapFullScreen) {
      this.getEventEmitter().emit(MapViewEvent.enterFullscreen);
    } else {
      this.getEventEmitter().emit(MapViewEvent.exitFullscreen);
    }
  }

  /**
   * @inheritDoc
   */
  public setInteractive(isInteractive: boolean): void {
    if (isInteractive) {
      this.map.dragPan.enable();
      this.map.doubleClickZoom.enable();
    } else {
      this.map.dragPan.disable();
      this.map.doubleClickZoom.disable();
    }
  }

  /**
   * Add marker to array
   */
  public addMarker(coordinates: MapCoordinatesInterface): void {
    this.markersCoordinates.push(coordinates);

    if (this.map) {
      this.initMarker(coordinates);
    }
  }

  /**
   * Set loading state of the map
   */
  protected setLoading = (isLoading: boolean): void => {
    if (this.placeholderElement) {
      // Toggle loading class of the placeholder
      this.placeholderElement.classList.toggle(this.cssClass.placeholderLoading, isLoading);
      // Toggle hidden class of the show map button
      if (this.showMapButtonElement) {
        this.showMapButtonElement.classList.toggle(this.cssClass.placeholderButtonHide, isLoading);
      }
    }
  };

  /**
   * Render Map
   */
  protected onLoadMapboxMapsCss = (): void => {
    import(
      /* webpackChunkName: "mapbox-gl" */
      'mapbox-gl'
    ).then((response) => {
      // Delete placeholder element
      if (this.placeholderElement) {
        this.placeholderElement.remove();
      }
      // Show map container
      this.mapContainer.classList.remove(this.cssClass.mapHidden);

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore - config is not in the type
      response.config.ACCESS_TOKEN = this.mapboxAccessToken;

      // Add rtl map plugin
      this.addRtlPlugin(response);

      this.initializeMap(response.Map);

      this.addMapControl(response.NavigationControl);

      this.MarkerConstructor = response.Marker;
      this.markersCoordinates.forEach((markerCoordinate) => this.initMarker(markerCoordinate));

      this.setInteractive(this.settings.interactive);

      // Emit event
      this.eventEmitter.emit(MapViewEvent.render);
      this.refreshHTML();
    });
  };

  /**
   * Convert zoom percentage to zoom value
   */
  protected getZoomValue(zoomPercentage: number): number {
    return Math.round((MapboxMapsView.ZOOM_MAX * zoomPercentage) / 100);
  }

  /**
   * Set DOM based on full screen state
   */
  protected refreshHTML(): void {
    // Hide 'enter full screen' button if it exists on the view
    if (this.buttonEnterFullscreenElement) {
      if (this.isMapFullScreen) {
        this.buttonEnterFullscreenElement.classList.add(this.cssClass.buttonHide);
      } else {
        this.buttonEnterFullscreenElement.classList.remove(this.cssClass.buttonHide);
      }
    }

    // Hide 'exit full screen' button if it exists on the view
    if (this.buttonExitFullscreenElement) {
      if (this.isMapFullScreen) {
        this.buttonExitFullscreenElement.classList.remove(this.cssClass.buttonHide);
      } else {
        this.buttonExitFullscreenElement.classList.add(this.cssClass.buttonHide);
      }
    }

    // Hide 'title' element if it exists on the view
    if (this.titleElement) {
      if (this.isMapFullScreen) {
        this.titleElement.classList.add(this.cssClass.titleHide);
      } else {
        this.titleElement.classList.remove(this.cssClass.titleHide);
      }
    }

    // Set full screen classes on the view
    if (this.isMapFullScreen) {
      this.mapContainer.classList.add(this.cssClass.containerFullscreen);
      this.mapElement.classList.add(this.cssClass.mapFullscreen);
    } else {
      this.mapContainer.classList.remove(this.cssClass.containerFullscreen);
      this.mapElement.classList.remove(this.cssClass.mapFullscreen);
    }

    // Refresh, re-center and reset map
    this.refresh();
    this.documentService.setScrollable(!this.isMapFullScreen);
    this.headerService.setVisible(!this.isMapFullScreen);
  }

  /**
   * Clicked button to enter full screen mode
   */
  protected onClickButtonEnterFullscreen = (e: Event): void => {
    e.preventDefault();

    if (!this.map) {
      return;
    }

    this.setFullScreen(true);
    this.setInteractive(true);
  };

  /**
   * Clicked button to exit full screen mode
   */
  protected onClickButtonExitFullscreen = (e: Event): void => {
    e.preventDefault();

    if (!this.map) {
      return;
    }

    this.setFullScreen(false);
    this.setInteractive(this.settings.interactive);
  };

  /**
   * Clicked button to render map desktop
   */
  protected onClickButtonShowMap = (e: Event): void => {
    e.preventDefault();
    // Show map
    this.showMap();

    this.statsService.ctaClick({
      cta: {
        customEvent: {
          label: 'Property Detail',
          action: 'Click:View on map',
          name: 'User Interaction',
        },
      },
    });
  };

  /**
   * Render map view
   */
  protected onRenderMapView = (): void => {
    if (this.settings.showInFullScreen) {
      this.setFullScreen(true);
      this.setInteractive(true);
    }
  };

  /**
   * Toggled fullscreen ON or OFF
   */
  protected onToggleFullscreen = (): void => {
    if (!this.map) {
      return;
    }

    if (this.isMapFullScreen) {
      this.scrollY =
        this.windowService.getNative().scrollY ||
        this.windowService.getNative().pageYOffset ||
        this.windowService.getNative().document.documentElement.scrollTop;
    }

    this.refreshHTML();

    if (!this.isMapFullScreen) {
      this.windowService.getNative().scrollTo(0, this.scrollY);
    }
  };

  /**
   * Add rtl map plugin
   */
  private addRtlPlugin(response: typeof mapbox): void {
    if (this.configGetLanguage.isRtl) {
      response.setRTLTextPlugin(MapboxMapsView.MAP_RTL_PLUGIN, this.onRtlPluginLoadingError);
    }
  }

  /**
   * Throw rtl plugin loading error
   */
  private onRtlPluginLoadingError = (e: Error): void => {
    if(e) {
      // eslint-disable-next-line no-console
      console.error(`Failed to load rtl plugin: ${e.message}`);
    }
  };

  /**
   * Mapbox map initialization
   */
  private initializeMap(Map: typeof mapbox.Map): void {
    this.map = new Map({
      container: this.mapElement,
      attributionControl: true,
      scrollZoom: this.settings.zoom.zoomOnScroll,
      doubleClickZoom: this.settings.doubleClickZoom,
      maxZoom: MapboxMapsView.ZOOM_MAX,
      style: this.configGetLanguage.isRtl ? MapboxMapsView.MAP_ID_RTL : MapboxMapsView.MAP_ID,
      center: [this.settings.center.coordinates.lon, this.settings.center.coordinates.lat],
      zoom: this.getZoomValue(this.settings.zoom.percentage),
    });
  }

  /**
   * Add mapbox control
   */
  private addMapControl(NavigationControl: typeof mapbox.NavigationControl): void {
    this.map.addControl(
      new NavigationControl({
        showCompass: false,
      }),
      MapboxMapsControlPositionTypeEnum.bottomLeft
    );
  }

  /**
   * Init and add marker
   */
  private initMarker(markerCoordinates: MapCoordinatesInterface): void {
    const markerIconUrl: string = this.settings.marker ? this.settings.marker.icon.url : MapboxMapsView.LOGO_MAKRER;

    const markerIcon: HTMLImageElement = this.documentService.getDocument().createElement('img');
    markerIcon.setAttribute('src', markerIconUrl);

    const marker: Marker = new this.MarkerConstructor({
      element: markerIcon,
    });

    marker.setLngLat([markerCoordinates.lon, markerCoordinates.lat]).addTo(this.map);

    this.markers.push(marker);
  }

  /**
   * Show map
   */
  private showMap(): void {
    // Fire show event
    this.getEventEmitter().emit(MapViewEvent.toggleShowMap);
    // Render map view
    this.render();
  }

  /**
   * Attach observers
   */
  private observers(): void {
    // Register show map events mobile/desktop
    this.domQuerySelectorAll(this.element, this.cssSelector.showMapButton).forEach((element) => {
      element.addEventListener(DomElementEvent.click, this.onClickButtonShowMap);
    });

    // Register full screen event
    this.getEventEmitter().addListener(MapViewEvent.toggleFullscreen, this.onToggleFullscreen);

    // Add listeners to existing elements
    if (this.buttonEnterFullscreenElement) {
      this.buttonEnterFullscreenElement.addEventListener(DomElementEvent.click, this.onClickButtonEnterFullscreen);
    }

    if (this.buttonExitFullscreenElement) {
      this.buttonExitFullscreenElement.addEventListener(DomElementEvent.click, this.onClickButtonExitFullscreen);
    }

    this.getEventEmitter().addListener(MapViewEvent.render, this.onRenderMapView);
  }
}
